import React, { useEffect, useState, useMemo, FC, useContext } from 'react';
import { Box, Button, Checkbox, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import MoodBarometerIntro from './MoodBarometerIntro';
import MoodBarometerComment from './MoodBarometerComment';
import SurveyService from '../../services/SurveyService';
import { constants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import SurveyUtils from '../../shared/SurveyUtils';
import Autolink from '../Autolink/Autolink';
import { SurveyRateUsButton } from '../SurveyRateUsButton/SurveyRateUsButton';
import { OpenInNew } from '@mui/icons-material';
import CssUtils from '../../shared/CssUtils';
import { useIsSurveyPreview } from '../../hooks/useIsSurveyPreview';

interface Props {
  id: string;
  primaryColor: string;
  secondaryColor: string;
  customFont: string;
  companyName: string;
  feedbackActive?: boolean | undefined;
  feedbackText?: string;
  feedbackTitle?: string;
  introText?: string;
  outroText?: string;
  outroTextNegative?: string;
  negativeText?: string;
  positiveText?: string;
  neutralText?: string;
  title?: string;
  platform?: string;
  platformUrl?: string;
  platformName?: string;
  contactEmail?: string;
  question?: string;
  shouldShowPrivacyNotice: boolean;
  privacyNoticeText: string;
  privacyNoticeLink: string;
}

const MoodBarometerContent: FC<Props> = (props) => {
  const {
    id,
    primaryColor,
    secondaryColor,
    customFont,
    companyName,
    title,
    feedbackActive,
    feedbackText,
    feedbackTitle,
    introText,
    outroText,
    outroTextNegative,
    negativeText,
    positiveText,
    neutralText,
    platform,
    platformUrl,
    platformName,
    contactEmail,
    question,
    shouldShowPrivacyNotice,
    privacyNoticeText,
    privacyNoticeLink,
  } = props;

  const theme = useTheme();
  const effectiveFontFamily = '' + theme.typography.fontFamily;
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isPreview = useIsSurveyPreview();

  const [currentPlatform, setCurrentPlatform] = useState<string>(platform || '');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedMood, setSelectedMood] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const [lastAnswerId, setLastAnswerId] = useState<string>('');
  const [privacyNoticeChecked, setPrivacyNoticeChecked] = useState(false);

  const isLastPage = currentPage === 2;
  const isFirstPage = currentPage === 0;

  useEffect(() => {
    if (isLastPage && feedbackActive && !isFeedbackSubmitted) {
      setIsFeedbackSubmitted(false);
    }
  }, [currentPage, feedbackActive, isFeedbackSubmitted]);

  const formatTextToString = (text: any) => {
    if (!text) return null;

    return text.replace(/(\r\n|\r|\n)/g, ' ');
  };

  const handlePageChange = (change: number) => setCurrentPage((prev) => prev + change);
  const handleSaveMood = async () => {
    setIsFeedbackSubmitted(true);
    await saveMood();
    setShowButton(false);
  };
  const handleUpdateMood = async () => {
    setIsFeedbackSubmitted(true);
    await updateMood();
    setShowButton(false);
  };

  const saveMood = async () => {
    try {
      if (isPreview) return;
      const answer = await SurveyService.saveTrendSurveyAnswer(
        id,
        selectedMood.toUpperCase(),
        formatTextToString(feedback),
        { has_accepted_privacy_notice: privacyNoticeChecked }
      );
      setLastAnswerId(answer.ID);
    } catch (error) {
      console.error('Ein Fehler ist beim Speichern der Antwort aufgetreten:', error);
    }
  };

  const updateMood = async () => {
    try {
      if (isPreview) return;
      await SurveyService.updateTrendSurveyAnswer(
        lastAnswerId,
        id,
        selectedMood.toUpperCase(),
        formatTextToString(feedback),
        { has_accepted_privacy_notice: privacyNoticeChecked }
      );
    } catch (error) {
      console.error('Ein Fehler ist beim Speichern der Antwort aufgetreten:', error);
    }
  };

  const renderIntro = () => {
    if (isFirstPage) {
      return (
        <Box className="survey-intro survey-intro--trend">
          <Typography
            className="survey-title survey-intro-title"
            variant="h4"
            sx={{
              color: primaryColor,
              marginBottom: theme.spacing(5),
              fontWeight: 'bold',
              fontFamily: effectiveFontFamily,
            }}
          >
            {title}
          </Typography>
          <Typography
            className="survey-intro-text"
            variant="h5"
            sx={{
              color: secondaryColor,
              pt: theme.spacing(3),
              mb: theme.spacing(3),
              fontFamily: effectiveFontFamily,
            }}
          >
            <Autolink text={introText} />
          </Typography>
        </Box>
      );
    }
  };

  const renderFeedbackIntro = () => {
    if (currentPage === 1 && title && negativeText && positiveText && neutralText) {
      return (
        <MoodBarometerIntro
          {...{
            primaryColor,
            secondaryColor,
            customFont: effectiveFontFamily,
            companyName,
            title,
            negativeText,
            positiveText,
            neutralText,
            question,
          }}
          onChange={setSelectedMood}
        />
      );
    }
  };

  const renderFeedbackPrivacy = () => {
    if (!shouldShowPrivacyNotice) return <></>;

    return (
      <Box className="survey-privacy-notice">
        <Typography className="survey-privacy-notice-text">
          <Checkbox
            className="survey-privacy-notice-checkbox"
            checked={privacyNoticeChecked}
            onChange={() => setPrivacyNoticeChecked((prev) => !prev)}
          ></Checkbox>
          {privacyNoticeText}{' '}
          <a
            href={privacyNoticeLink}
            target="_blank"
            rel="noreferrer"
          >
            <OpenInNew
              fontSize="small"
              color="primary"
            ></OpenInNew>
          </a>
        </Typography>
      </Box>
    );
  };

  const renderFeedbackComment = () => {
    if (
      currentPage === 2 &&
      feedbackActive &&
      !isFeedbackSubmitted &&
      feedbackText &&
      feedbackTitle &&
      title &&
      selectedMood !== 'positive'
    ) {
      return (
        <>
          <MoodBarometerComment
            {...{
              secondaryColor,
              primaryColor,
              customFont: effectiveFontFamily,
              feedbackText,
              feedbackTitle,
              title,
            }}
            onChange={setFeedback}
          />

          {renderFeedbackPrivacy()}
        </>
      );
    }
  };

  const renderFeedbackOutro = () => {
    if ((currentPage === 2 && !feedbackActive) || isFeedbackSubmitted) {
      return (
        <Box
          className="survey-outro survey-outro--trend"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            className="survey-title survey-outro-title"
            variant="h4"
            sx={{
              color: primaryColor,
              marginBottom: isLastPage ? theme.spacing(4) : theme.spacing(3),
              fontWeight: 'bold',
              fontFamily: effectiveFontFamily,
            }}
          >
            {selectedMood === 'positive' ? (
              <Autolink text={outroText} />
            ) : (
              <Autolink text={outroTextNegative ? outroTextNegative : outroText} />
            )}
          </Typography>
          {contactEmail && selectedMood === 'negative' && !feedbackActive && (
            <Typography
              className="survey-contact-email"
              sx={{
                color: secondaryColor,
                fontWeight: 'bold',
                fontSize: '1.1rem',
                mt: 4,
                fontFamily: effectiveFontFamily,
              }}
            >
              {contactEmail ? `${t('appreciate_feedback')}: ${contactEmail}` : null}
            </Typography>
          )}
          {platform && selectedMood === 'positive' && (
            <Box
              sx={{
                color: primaryColor,
                marginBottom: isLastPage ? theme.spacing(-4) : theme.spacing(3),
                fontWeight: 'bold',
                mt: theme.spacing(2),
                fontFamily: effectiveFontFamily,
              }}
            ></Box>
          )}
          {platformUrl && selectedMood === 'positive' && (
            <>
              <Box
                className="survey-rate-us-wrapper"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'fit-content',
                  margin: '0 auto',
                  mb: 4,
                }}
              >
                <SurveyRateUsButton
                  surveyId={id}
                  surveyType={'trend'}
                  platformUrl={platformUrl}
                  platform={currentPlatform ?? ''}
                  platformName={platformName}
                  theme={theme}
                  primary={primaryColor}
                  customFont={effectiveFontFamily}
                ></SurveyRateUsButton>
              </Box>
            </>
          )}
        </Box>
      );
    }
  };

  const finishedAfterRating = !feedbackActive || selectedMood === 'positive';
  return (
    <Box
      className="survey-content survey-content--trend"
      sx={CssUtils.surveyContent()}
    >
      {renderIntro()}
      {renderFeedbackIntro()}
      {renderFeedbackOutro()}
      {renderFeedbackComment()}
      <Box
        className="survey-navigation survey-navigation--trend"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          textAlign: 'center',
          mt: theme.spacing(4),
        }}
      >
        {!isLastPage && showButton && (
          <Button
            className={
              currentPage === 1
                ? 'survey-button survey-button--send survey-button--trend-mood'
                : 'survey-button survey-button--next survey-button--trend-intro'
            }
            variant="contained"
            disabled={selectedMood === '' && currentPage === 1}
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => {
              handlePageChange(1);
              if (currentPage === 1) {
                if (finishedAfterRating) {
                  handleSaveMood();
                } else {
                  saveMood();
                }
              }
            }}
          >
            {currentPage === 1 ? t('confirm') : t('next')}
          </Button>
        )}

        {isLastPage && !finishedAfterRating && !isFeedbackSubmitted && (
          <Button
            className="survey-button survey-button--send survey-button--trend-feedback"
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={handleUpdateMood}
            disabled={shouldShowPrivacyNotice && !privacyNoticeChecked && !!feedback}
          >
            {t('send')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MoodBarometerContent;
